import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable} from "mobx";
import {DateTime} from "luxon";
import {Bindings} from "data/constants/bindings";
import {LeaderboardType, ModalType} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {uniq} from "lodash";
import {GAME_TIMEZONE} from "data/constants";

export interface ILeaderboardNavigationController<T = void> extends ViewController<T> {
	toNext: () => void;
	toPrev: () => void;
	openDayPicker: () => void;

	get selectorDate(): string;

	get isNextDisabled(): boolean;

	get isPrevDisabled(): boolean;
	get contestIds(): number[];
}

@injectable()
export class LeaderboardNavigationController implements ILeaderboardNavigationController {
	constructor(
		@inject(Bindings.ContestsStore) protected readonly _contestsStore: IContestsStore,
		@inject(Bindings.RankingsStore) protected readonly _rankingsStore: IRankingsStore,
		@inject(Bindings.RoundsStore) protected readonly _roundsStore: IRoundsStore,
		@inject(Bindings.ModalsStore) protected readonly _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get selectorDate(): string {
		if (this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily) {
			return this.contestDay;
		}
		return this.roundDate;
	}

	get isNextDisabled(): boolean {
		if (this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily) {
			return !this._contestsStore.isNextContestAvailable;
		}

		return !this.nextRoundContestId;
	}

	get isPrevDisabled(): boolean {
		if (this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily) {
			return !this._contestsStore.isPrevContestAvailable;
		}

		return !this.prevRoundContestId;
	}

	get contestIds(): number[] {
		return [];
	}

	private get contestDay(): string {
		const currentContest = this._contestsStore.currentContest;
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	private get currentRound() {
		const currentContest = this._contestsStore.currentContest;
		if (currentContest) {
			return this._roundsStore.rounds.find((it) => it.id === currentContest.roundId);
		}
		return this._roundsStore.activeRound;
	}

	private get roundDate(): string {
		const currentRound = this.currentRound;
		if (!currentRound) {
			return "-";
		}
		const startDate = DateTime.fromISO(currentRound.startDate)
			.setZone(GAME_TIMEZONE)
			.toFormat("L/dd");
		const endDate = DateTime.fromJSDate(new Date(currentRound.endDate))
			.setZone(GAME_TIMEZONE)
			.toFormat("L/dd");
		return `${startDate} - ${endDate}`;
	}

	public toNext = () => {
		if (this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily) {
			this._contestsStore.toNextContest();
			return;
		}

		const nextRoundContestId = this.nextRoundContestId;
		if (nextRoundContestId) {
			this._contestsStore.toContestId(nextRoundContestId);
		}
	};

	public toPrev = () => {
		if (this._rankingsStore.activeLeaderboardType === LeaderboardType.Daily) {
			this._contestsStore.toPrevContest();
			return;
		}

		const prevRoundContestId = this.prevRoundContestId;
		if (prevRoundContestId) {
			this._contestsStore.toContestId(prevRoundContestId);
		}
	};

	public openDayPicker = () => {
		this._modalStore.showModal(ModalType.DAY_PICKER);
	};

	private get nextRoundContestId() {
		const currentRound = this.currentRound;
		const rounds = this.gameRoundIds;
		const currentRoundIdIndex = rounds.findIndex((it) => it === currentRound?.id);
		const nextRound = rounds[currentRoundIdIndex + 1];

		return this._contestsStore.gameContests.findLast((contest) => contest.roundId === nextRound)
			?.id;
	}

	private get prevRoundContestId() {
		const currentRound = this.currentRound;
		const rounds = this.gameRoundIds;
		const currentRoundIdIndex = rounds.findIndex((it) => it === currentRound?.id);
		const prevRound = rounds[currentRoundIdIndex - 1];

		return this._contestsStore.gameContests.findLast((contest) => contest.roundId === prevRound)
			?.id;
	}

	get gameRoundIds() {
		return uniq(this._contestsStore.gameContests.map((it) => it.roundId));
	}

	dispose(): void {
		this._contestsStore.toContestId(this._contestsStore.currentGameContest?.id || 0);
	}

	init(param: void): void {
		return;
	}

	onChange(param: void): void {
		return;
	}
}
